import qs from "qs";
import type { Location } from "react-router";
import type { ImpersonationTargetType } from "@entrilia/shared/api/types";
import type { BcRole } from "../api/types/bcTypes";
import type { BusinessCentralSettings } from "../api/types/clientTypes";
import { pageRoutes } from "../routes";
import { getVariables } from "../variables";

interface ReferrerLocationState {
  referrerPath: string;
}

export const getReferrerLocationState = (location: Location): ReferrerLocationState => ({
  referrerPath: `${location.pathname}${location.search}`,
});

export const getReferrerPath = (locationState: unknown): string | undefined => {
  if (typeof locationState !== "object" || locationState === null || !("referrerPath" in locationState)) {
    return undefined;
  }

  return typeof locationState.referrerPath === "string" ? locationState.referrerPath : undefined;
};

export interface ImpersonationNavigationParams {
  target: ImpersonationTargetType;
  userEmail?: string;
  fundraisingId?: string;
}

const getSubPathAndTargetParamForImpersonation = (params: ImpersonationNavigationParams) => {
  switch (params.target) {
    case "Investor": {
      return { subPath: "", target: params.target.toLowerCase() };
    }
    case "Fundraising": {
      return {
        subPath: "/fundraising",
        target: params.fundraisingId
          ? `${params.target.toLowerCase()}:${params.fundraisingId}`
          : params.target.toLowerCase(),
      };
    }
    case "DataCollection": {
      return { subPath: "/data-collection", target: params.target.toLowerCase() };
    }
    default: {
      return { subPath: "", target: "investor" };
    }
  }
};

export const getInvestorPortalImpersonationUrl = (
  clientCode: string,
  params: ImpersonationNavigationParams
): string => {
  const { subPath, target } = getSubPathAndTargetParamForImpersonation(params);
  const user = params.userEmail || "any";
  const queryParams = { impersonation: "true", user, target };
  return `${getVariables().IR_PORTAL_URI}/${clientCode}${subPath}?${qs.stringify(queryParams)}`;
};

const BUSINESS_CENTRAL_ORIGIN = "https://businesscentral.dynamics.com";

export const parseBusinessCentralUrl = (value: string | null): string | undefined => {
  if (!value || !URL.canParse(value)) {
    return undefined;
  }
  return new URL(value).origin === BUSINESS_CENTRAL_ORIGIN ? value : undefined;
};

const getBusinessCentralBaseUrl = (bcSettings: BusinessCentralSettings) => {
  const { tenantId, environment } = bcSettings;
  return `${BUSINESS_CENTRAL_ORIGIN}/${tenantId}/${environment}`;
};

const buildBusinessCentralRedirectUrl = (clientCode: string, redirectUrl: string, role?: BcRole): string =>
  `/${clientCode}/${pageRoutes.bcRedirect}?${qs.stringify({ redirectUrl, role })}`;

export const getBusinessCentralRedirectUrl = (
  clientCode: string,
  bcSettings: BusinessCentralSettings,
  role?: BcRole
): string => {
  const bcBaseUrl = getBusinessCentralBaseUrl(bcSettings);
  const bcUrl = `${bcBaseUrl}?${qs.stringify({ company: bcSettings.companyName, profile: role })}`;
  return buildBusinessCentralRedirectUrl(clientCode, bcUrl, role);
};

// Routes an already-resolved Business Central transaction URL through the bc-redirect page so the
// existing access-ensure flow runs (Fund Operations role) before navigating to BC.
export const getFundOpsBusinessCentralRedirectUrl = (clientCode: string, resolvedBcUrl: string): string =>
  buildBusinessCentralRedirectUrl(clientCode, resolvedBcUrl, "EAC Fund Accountant");

export const getObjectPageRoute = (
  clientCode: string,
  objectId: string,
  objectType: string,
  entityTypeCode?: string
): string => {
  switch (objectType) {
    case "Fund": {
      return `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.funds}/${objectId}/main`;
    }
    case "Investor": {
      return `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investors}/${objectId}/main`;
    }
    case "Contact": {
      return `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.contacts}/${objectId}/main`;
    }
    case "PortfolioCompany": {
      return `/${clientCode}/${pageRoutes.portfolio}/${pageRoutes.portfolioCompanies}/${objectId}`;
    }
    case "FundInvestment": {
      return `/${clientCode}/${pageRoutes.portfolio}/${pageRoutes.fundInvestments}/${objectId}`;
    }
    case "Vendor": {
      return `/${clientCode}/${pageRoutes.expenseManagement}/${pageRoutes.vendors}/${objectId}`;
    }
    case "Invoice": {
      return `/${clientCode}/${pageRoutes.expenseManagement}/${pageRoutes.invoices}/${objectId}`;
    }
    case "InvoiceVendor": {
      if (entityTypeCode?.toUpperCase() === "VENDOR") {
        return `/${clientCode}/${pageRoutes.expenseManagement}/${pageRoutes.vendors}/${objectId}`;
      }

      return "";
    }
    case "Entity":
    case "VatBusinessPostingGroup": {
      return "";
    }
    default: {
      return `/${clientCode}/${pageRoutes.settings}/${pageRoutes.records}/${objectType}/${objectId}`;
    }
  }
};
